import { FetchError } from '../../classes/FetchError';
import { ErrorResponseJSONInterface } from '../../interfaces/ErrorResponseJSONInterface';
import { loginRedirect } from '../../utils/loginRedirect';

export async function fetcher<Data, RawData>(
  url: string,
  skipRedirectOn401?: boolean,
  dangerouslyUsePOSTWithPayload?: string,
  headers?: string,
  deserializer?: (data: RawData) => Data,
): Promise<Data | undefined> {
  try {
    const response = await fetch(url, {
      credentials: 'include',
      method: dangerouslyUsePOSTWithPayload ? 'POST' : 'GET',
      body: dangerouslyUsePOSTWithPayload,
      headers: {
        ...(dangerouslyUsePOSTWithPayload && {
          'Content-Type': 'application/json',
        }),
        ...(headers && JSON.parse(headers)),
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (deserializer) {
        return deserializer(data as RawData) as Data;
      } else {
        return data as Data;
      }
    }

    // If we got a 401, redirect to login page
    if (response.status === 401 && !skipRedirectOn401) {
      loginRedirect();
      return undefined;
    }

    let errorData: ErrorResponseJSONInterface | undefined;
    try {
      if (response.headers.get('Content-Type')?.includes('application/json')) {
        errorData = (await response.json()) as ErrorResponseJSONInterface;
      } else {
        errorData = undefined;
      }
    } catch {
      // WARNING: there is no guarantee that this JSON has the right format
      errorData = undefined;
    }

    throw new FetchError(response.statusText, response.status, errorData);
  } catch (e) {
    if (e instanceof FetchError) {
      throw e;
    }
    // The only error which can be thrown here is a TypeError,
    // SOURCE: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
    const error = e as { message?: string };
    throw new FetchError(error.message || 'unknown error');
  }
}
